import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Checkbox } from '@/components/ui/checkbox';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
    Activity,
    AlertTriangle,
    Bell,
    CheckCircle,
    Clock,
    Edit,
    Info,
    Pause,
    Play,
    Plus,
    Target,
    TestTube,
    Trash2,
    Users,
    Zap,
} from 'lucide-react';
import { useSnackbar } from 'notistack';
import { useConfirm } from '@/hooks/useConfirm';
import {
    type NotificationTemplate,
    useNotificationTemplates,
} from '@/api/notifications/use-notifications.tsx';
import DateTimeFormatter from '@/components/shared/DateTimeFormatter';
import {
    type NotificationRule,
    useAvailableTriggers,
    useCreateNotificationRule,
    useDeleteNotificationRule,
    useTestNotificationRule,
    useToggleNotificationRule,
    useUpdateNotificationRule,
    getRecipientOptions,
} from '@/api/notifications/use-notification-rules.tsx';
import { getTriggerVariables, GENERAL_VARIABLES } from '@/data/notification-trigger-variables';

interface AutomationRulesManagerProps {
    rules: NotificationRule[];
    templates: NotificationTemplate[];
    isLoading: boolean;
    onRefresh: () => void;
}

export const AutomationRulesManager: React.FC<AutomationRulesManagerProps> = ({
    rules = [],
    templates = [],
    isLoading,
    onRefresh,
}) => {
    const { enqueueSnackbar } = useSnackbar();
    const confirm = useConfirm();
    const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
    const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
    const [isTestDialogOpen, setIsTestDialogOpen] = useState(false);
    const [selectedRule, setSelectedRule] = useState<NotificationRule | null>(null);
    const [activeTab, setActiveTab] = useState('rules');

    // Fetch all templates for dropdown (without pagination)
    const { data: allTemplatesResponse, isLoading: allTemplatesLoading } = useNotificationTemplates(
        {
            active_only: false,
            search: '',
            page: 1,
            per_page: 1000, // Fetch all templates for dropdown
        }
    );

    // Use all templates for dropdown, fallback to passed templates if not loaded yet
    const allTemplates = Array.isArray(allTemplatesResponse?.data)
        ? allTemplatesResponse.data
        : Array.isArray(templates)
          ? templates
          : [];

    // Ensure arrays are always arrays
    const safeRules = Array.isArray(rules) ? rules : [];
    const safeTemplates = allTemplates;

    const createMutation = useCreateNotificationRule();
    const updateMutation = useUpdateNotificationRule();
    const deleteMutation = useDeleteNotificationRule();
    const toggleMutation = useToggleNotificationRule();
    const testMutation = useTestNotificationRule();
    const {
        data: availableTriggersResponse,
        isLoading: triggersLoading,
        error: triggersError,
    } = useAvailableTriggers();

    // Convert backend response format to frontend expected format
    const availableTriggers = availableTriggersResponse?.data
        ? Object.keys(availableTriggersResponse.data)
        : [];

    // Fallback triggers if API is not available
    const fallbackTriggers = [
        'customer_registered',
        'customer_created_by_crm',
        'kyc_approval',
        'deposit_requested',
        'deposit_received',
        'bonus_received',
        'withdrawal_requested',
        'withdrawal_completed',
        'trade_opened',
        'trade_closed',
        'large_position',
        'kyc_expiry',
        'kyc_failed',
        'suspicious_activity',
        'account_locked',
        'system_maintenance',
        'margin_call',
        'high_risk_transaction',
    ];

    // Fallback trigger descriptions
    const fallbackTriggerDescriptions = {
        customer_registered: 'When a customer completes registration',
        customer_created_by_crm: 'When a CRM user creates a customer',
        kyc_approval: 'When KYC verification is approved',
        deposit_requested: 'When a deposit is requested/initiated',
        deposit_received: 'When a deposit is confirmed/completed',
        bonus_received: 'When a bonus is credited to account',
        withdrawal_requested: 'When a withdrawal is requested',
        withdrawal_completed: 'When a withdrawal is approved/completed',
        trade_opened: 'When a trade position is opened',
        trade_closed: 'When a trade position is closed',
        large_position: 'When a large trading position is detected',
        kyc_expiry: 'When KYC documents are expiring',
        kyc_failed: 'When KYC verification fails',
        suspicious_activity: 'When suspicious activity is detected',
        account_locked: 'When an account is locked',
        system_maintenance: 'When system maintenance is scheduled',
        margin_call: 'When a margin call is triggered',
        high_risk_transaction: 'When a high-risk transaction occurs',
    };

    // Use fallback if no triggers from API
    const finalAvailableTriggers =
        availableTriggers.length > 0 ? availableTriggers : fallbackTriggers;

    // Use fallback descriptions if API descriptions are not available
    const finalTriggerDescriptions = availableTriggersResponse?.data || fallbackTriggerDescriptions;

    // Debug logging
    console.log('availableTriggersResponse:', availableTriggersResponse);
    console.log('availableTriggers:', availableTriggers);
    console.log('finalAvailableTriggers:', finalAvailableTriggers);

    const handleCreateRule = async (data: any) => {
        try {
            await createMutation.mutateAsync(data);
            enqueueSnackbar('Automation rule created successfully', { variant: 'success' });
            setIsCreateDialogOpen(false);
            onRefresh();
        } catch (error) {
            enqueueSnackbar('Failed to create automation rule', { variant: 'error' });
        }
    };

    const handleUpdateRule = async (id: string, data: any) => {
        if (!id || id.trim() === '') {
            enqueueSnackbar('Invalid rule ID', { variant: 'error' });
            return;
        }

        try {
            await updateMutation.mutateAsync({ id, data });
            enqueueSnackbar('Automation rule updated successfully', { variant: 'success' });
            setIsEditDialogOpen(false);
            setSelectedRule(null);
            onRefresh();
        } catch (error) {
            enqueueSnackbar('Failed to update automation rule', { variant: 'error' });
        }
    };

    const handleDeleteRule = async (rule: NotificationRule) => {
        if (!rule.id || rule.id.trim() === '') {
            enqueueSnackbar('Invalid rule ID', { variant: 'error' });
            return;
        }

        const ok = await confirm({
            title: 'Delete automation rule?',
            description: `"${rule.name}" will be permanently removed.`,
            confirmLabel: 'Delete',
            variant: 'destructive',
        });
        if (!ok) return;

        try {
            await deleteMutation.mutateAsync(rule.id);
            enqueueSnackbar('Automation rule deleted successfully', { variant: 'success' });
            onRefresh();
        } catch (error) {
            enqueueSnackbar('Failed to delete automation rule', { variant: 'error' });
        }
    };

    const handleToggleRule = async (id: string) => {
        if (!id || id.trim() === '') {
            enqueueSnackbar('Invalid rule ID', { variant: 'error' });
            return;
        }

        try {
            await toggleMutation.mutateAsync(id);
            enqueueSnackbar('Automation rule toggled successfully', { variant: 'success' });
            onRefresh();
        } catch (error) {
            enqueueSnackbar('Failed to toggle automation rule', { variant: 'error' });
        }
    };

    const handleTestRule = async (id: string, testData: any) => {
        if (!id || id.trim() === '') {
            enqueueSnackbar('Invalid rule ID', { variant: 'error' });
            return;
        }

        try {
            await testMutation.mutateAsync({ id, data: { data: testData } });
            enqueueSnackbar('Test notification sent successfully', { variant: 'success' });
            setIsTestDialogOpen(false);
        } catch (error) {
            enqueueSnackbar('Failed to send test notification', { variant: 'error' });
        }
    };

    const getTriggerIcon = (trigger: string) => {
        switch (trigger) {
            case 'customer_registered':
            case 'customer_created_by_crm':
            case 'kyc_approval':
                return <Users className="h-4 w-4" />;
            case 'trade_opened':
            case 'trade_closed':
            case 'large_position':
            case 'margin_call':
                return <Target className="h-4 w-4" />;
            case 'kyc_expiry':
            case 'kyc_failed':
                return <AlertTriangle className="h-4 w-4" />;
            case 'deposit_requested':
            case 'deposit_received':
            case 'withdrawal_requested':
            case 'withdrawal_completed':
                return <CheckCircle className="h-4 w-4" />;
            default:
                return <Bell className="h-4 w-4" />;
        }
    };

    const getTriggerLabel = (trigger: string) => {
        return trigger
            .split('_')
            .map(word => word.charAt(0).toUpperCase() + word.slice(1))
            .join(' ');
    };

    if (isLoading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="text-center">
                    <Zap className="h-8 w-8 mx-auto mb-4 text-muted-foreground animate-pulse" />
                    <p className="text-muted-foreground">Loading automation rules...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="p-4">
            <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold">Workflows & Triggers</h2>
                <div className="flex items-center gap-2">
                    {triggersLoading && (
                        <div className="text-sm text-muted-foreground flex items-center gap-1">
                            <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-primary"></div>
                            Loading triggers...
                        </div>
                    )}
                    {triggersError && (
                        <div className="text-sm text-warning flex items-center gap-1">
                            <AlertTriangle className="h-3 w-3" />
                            Using fallback triggers
                        </div>
                    )}
                    <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
                        <DialogTrigger asChild>
                            <Button>
                                <Plus className="h-4 w-4" />
                                Create Rule
                            </Button>
                        </DialogTrigger>
                        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
                            <DialogHeader>
                                <DialogTitle>Create Automation Rule</DialogTitle>
                            </DialogHeader>
                            <CreateRuleForm
                                onSubmit={handleCreateRule}
                                templates={safeTemplates}
                                availableTriggers={finalAvailableTriggers}
                                triggerDescriptions={finalTriggerDescriptions}
                            />
                        </DialogContent>
                    </Dialog>
                </div>
            </div>

            <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
                <TabsList className="grid w-full grid-cols-1">
                    <TabsTrigger value="rules" className="flex items-center gap-2">
                        <Zap className="h-4 w-4" />
                        Rules
                    </TabsTrigger>
                </TabsList>

                <TabsContent value="rules" className="space-y-4">
                    <div className="space-y-4 pr-2">
                        {safeRules.map(rule => (
                            <Card key={rule.id} className="hover:shadow-md transition-shadow">
                                <CardHeader className="pb-3">
                                    <div className="flex items-center justify-between">
                                        <div className="flex items-center gap-2">
                                            {getTriggerIcon(rule.trigger)}
                                            <CardTitle className="text-lg">{rule.name}</CardTitle>
                                        </div>
                                        <div className="flex items-center gap-1">
                                            <Button
                                                variant="ghost"
                                                size="sm"
                                                onClick={() => {
                                                    setSelectedRule(rule);
                                                    setIsTestDialogOpen(true);
                                                }}
                                                disabled={testMutation.isPending}
                                                aria-label={`Test ${rule.name}`}
                                                title="Test rule"
                                            >
                                                <TestTube className="h-4 w-4" />
                                            </Button>
                                            <Button
                                                variant="ghost"
                                                size="sm"
                                                onClick={() => handleToggleRule(rule.id)}
                                                disabled={toggleMutation.isPending}
                                                aria-label={
                                                    rule.is_active
                                                        ? `Pause ${rule.name}`
                                                        : `Activate ${rule.name}`
                                                }
                                                title={
                                                    rule.is_active ? 'Pause rule' : 'Activate rule'
                                                }
                                            >
                                                {rule.is_active ? (
                                                    <Pause className="h-4 w-4" />
                                                ) : (
                                                    <Play className="h-4 w-4" />
                                                )}
                                            </Button>
                                            <Button
                                                variant="ghost"
                                                size="sm"
                                                onClick={() => {
                                                    setSelectedRule(rule);
                                                    setIsEditDialogOpen(true);
                                                }}
                                                aria-label={`Edit ${rule.name}`}
                                                title="Edit rule"
                                            >
                                                <Edit className="h-4 w-4" />
                                            </Button>
                                            <Button
                                                variant="ghost"
                                                size="sm"
                                                onClick={() => handleDeleteRule(rule)}
                                                disabled={deleteMutation.isPending}
                                                aria-label={`Delete ${rule.name}`}
                                                title="Delete rule"
                                            >
                                                <Trash2 className="h-4 w-4" />
                                            </Button>
                                        </div>
                                    </div>
                                </CardHeader>
                                <CardContent>
                                    <div className="space-y-3">
                                        <div className="flex items-center gap-2">
                                            {rule.is_active ? (
                                                <Badge
                                                    variant="default"
                                                    className="bg-success text-success-foreground"
                                                >
                                                    Active
                                                </Badge>
                                            ) : (
                                                <Badge variant="secondary">Inactive</Badge>
                                            )}
                                        </div>

                                        <div className="text-sm text-muted-foreground">
                                            <div className="flex items-center gap-1 mb-1">
                                                <Bell className="h-3 w-3" />
                                                <span>
                                                    Trigger: {getTriggerLabel(rule.trigger)}
                                                </span>
                                            </div>
                                        </div>

                                        <div className="text-xs text-muted-foreground">
                                            <div className="flex items-center gap-1 mb-1">
                                                <Users className="h-3 w-3" />
                                                <span>
                                                    Recipients: {rule.recipients?.length || 0}
                                                </span>
                                            </div>
                                            {rule.template && (
                                                <div className="flex items-center gap-1">
                                                    <span>Template: {rule.template.name}</span>
                                                </div>
                                            )}
                                        </div>

                                        <div className="flex items-center gap-2 text-xs text-muted-foreground">
                                            <div className="flex items-center gap-1">
                                                <Activity className="h-3 w-3" />
                                                <span>{rule.trigger_count} triggers</span>
                                            </div>
                                            {rule.last_triggered_at && (
                                                <div className="flex items-center gap-1">
                                                    <Clock className="h-3 w-3" />
                                                    <span>
                                                        Last:{' '}
                                                        <DateTimeFormatter
                                                            date={rule.last_triggered_at}
                                                        />
                                                    </span>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </CardContent>
                            </Card>
                        ))}

                        {safeRules.length === 0 && (
                            <div className="flex items-center justify-center h-64">
                                <div className="text-center">
                                    <Zap className="h-8 w-8 mx-auto mb-4 text-muted-foreground" />
                                    <h3 className="text-lg font-medium mb-2">
                                        No automation rules
                                    </h3>
                                    <p className="text-muted-foreground mb-4">
                                        Create your first automation rule
                                    </p>
                                    <Button onClick={() => setIsCreateDialogOpen(true)}>
                                        <Plus className="h-4 w-4" />
                                        Create Rule
                                    </Button>
                                </div>
                            </div>
                        )}
                    </div>
                </TabsContent>
            </Tabs>

            {/* Edit Dialog */}
            <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
                <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle>Edit Automation Rule</DialogTitle>
                    </DialogHeader>
                    {selectedRule && (
                        <EditRuleForm
                            rule={selectedRule}
                            onSubmit={data => handleUpdateRule(selectedRule.id, data)}
                            templates={safeTemplates}
                            availableTriggers={finalAvailableTriggers}
                            triggerDescriptions={finalTriggerDescriptions}
                        />
                    )}
                </DialogContent>
            </Dialog>

            {/* Test Dialog */}
            <Dialog open={isTestDialogOpen} onOpenChange={setIsTestDialogOpen}>
                <DialogContent className="max-w-2xl">
                    <DialogHeader>
                        <DialogTitle>Test Rule</DialogTitle>
                    </DialogHeader>
                    {selectedRule && (
                        <TestRuleForm
                            rule={selectedRule}
                            onSubmit={testData => handleTestRule(selectedRule.id, testData)}
                        />
                    )}
                </DialogContent>
            </Dialog>
        </div>
    );
};

// Simplified Create Rule Form Component
const CreateRuleForm: React.FC<{
    onSubmit: (data: any) => void;
    templates: NotificationTemplate[];
    availableTriggers: string[];
    triggerDescriptions: Record<string, string>;
}> = ({ onSubmit, templates = [], availableTriggers, triggerDescriptions }) => {
    const [formData, setFormData] = useState({
        name: '',
        trigger: '',
        template_id: '',
        recipients: [] as string[],
        is_active: true,
    });

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        onSubmit(formData);
    };

    const recipientOptions = getRecipientOptions();

    return (
        <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid grid-cols-2 gap-4">
                <div>
                    <label className="text-sm font-medium">Rule Name</label>
                    <Input
                        value={formData.name}
                        onChange={e => setFormData({ ...formData, name: e.target.value })}
                        placeholder="e.g., Large Trade Alert"
                        required
                    />
                </div>
                <div>
                    <label className="text-sm font-medium">Trigger Event</label>
                    <Select
                        value={formData.trigger}
                        onValueChange={value => setFormData({ ...formData, trigger: value })}
                    >
                        <SelectTrigger>
                            <SelectValue placeholder="Select trigger" />
                        </SelectTrigger>
                        <SelectContent>
                            {availableTriggers.map(trigger => (
                                <SelectItem key={trigger} value={trigger}>
                                    {triggerDescriptions[trigger] ||
                                        trigger
                                            .split('_')
                                            .map(
                                                word => word.charAt(0).toUpperCase() + word.slice(1)
                                            )
                                            .join(' ')}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </div>
            </div>

            {/* Show available variables when trigger is selected */}
            {formData.trigger && (
                <Card className="bg-info/10 border-info/20 text-foreground">
                    <CardHeader className="pb-3">
                        <CardTitle className="text-sm flex items-center gap-2">
                            <Info className="h-4 w-4" />
                            Available Variables for Template
                        </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-3">
                        <div>
                            <p className="text-xs font-semibold text-muted-foreground mb-2">
                                General Variables (available to all templates):
                            </p>
                            <div className="flex flex-wrap gap-1">
                                {GENERAL_VARIABLES.map(variable => (
                                    <Badge
                                        key={variable.name}
                                        variant="secondary"
                                        className="text-xs font-mono cursor-help"
                                        title={variable.description}
                                    >
                                        {`{{${variable.name}}}`}
                                    </Badge>
                                ))}
                            </div>
                        </div>
                        <div>
                            <p className="text-xs font-semibold text-muted-foreground mb-2">
                                Trigger-Specific Variables:
                            </p>
                            <div className="flex flex-wrap gap-1">
                                {getTriggerVariables(formData.trigger)
                                    .filter(v => !GENERAL_VARIABLES.some(gv => gv.name === v.name))
                                    .map(variable => (
                                        <Badge
                                            key={variable.name}
                                            variant="outline"
                                            className="text-xs font-mono cursor-help"
                                            title={variable.description}
                                        >
                                            {`{{${variable.name}}}`}
                                        </Badge>
                                    ))}
                            </div>
                        </div>
                        <p className="text-xs text-muted-foreground italic">
                            💡 Tip: Hover over a variable to see its description. Use these
                            variables in your notification template with the{' '}
                            <code className="bg-muted px-1 rounded">{`{{variable_name}}`}</code>{' '}
                            syntax.
                        </p>
                    </CardContent>
                </Card>
            )}

            <div>
                <label className="text-sm font-medium">Notification Template</label>
                <Select
                    value={formData.template_id}
                    onValueChange={value => setFormData({ ...formData, template_id: value })}
                >
                    <SelectTrigger>
                        <SelectValue placeholder="Select template" />
                    </SelectTrigger>
                    <SelectContent>
                        {templates
                            .filter(t => t.type === 'automatic')
                            .map(template => (
                                <SelectItem key={template.id} value={template.id}>
                                    {template.name}
                                </SelectItem>
                            ))}
                    </SelectContent>
                </Select>
            </div>

            <div>
                <label className="text-sm font-medium mb-2 block">Recipients</label>
                <div className="grid grid-cols-2 gap-2">
                    {recipientOptions.map(recipient => (
                        <div key={recipient.value} className="flex items-center space-x-2">
                            <Checkbox
                                id={recipient.value}
                                checked={formData.recipients.includes(recipient.value)}
                                onCheckedChange={checked => {
                                    if (checked) {
                                        setFormData({
                                            ...formData,
                                            recipients: [...formData.recipients, recipient.value],
                                        });
                                    } else {
                                        setFormData({
                                            ...formData,
                                            recipients: formData.recipients.filter(
                                                r => r !== recipient.value
                                            ),
                                        });
                                    }
                                }}
                            />
                            <label htmlFor={recipient.value} className="text-sm">
                                {recipient.label}
                            </label>
                        </div>
                    ))}
                </div>
            </div>

            <div className="flex items-center space-x-2">
                <Switch
                    checked={formData.is_active}
                    onCheckedChange={checked => setFormData({ ...formData, is_active: checked })}
                />
                <label className="text-sm font-medium">Active</label>
            </div>

            <div className="flex justify-end gap-2">
                <Button type="button" variant="outline">
                    Cancel
                </Button>
                <Button type="submit">Create Rule</Button>
            </div>
        </form>
    );
};

// Simplified Edit Rule Form Component
const EditRuleForm: React.FC<{
    rule: NotificationRule;
    onSubmit: (data: any) => void;
    templates: NotificationTemplate[];
    availableTriggers: string[];
    triggerDescriptions: Record<string, string>;
}> = ({ rule, onSubmit, templates = [], availableTriggers, triggerDescriptions }) => {
    const [formData, setFormData] = useState({
        name: rule.name,
        trigger: rule.trigger,
        template_id: rule.template_id,
        recipients: rule.recipients || [],
        is_active: rule.is_active,
    });

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        onSubmit(formData);
    };

    const recipientOptions = getRecipientOptions();

    return (
        <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid grid-cols-2 gap-4">
                <div>
                    <label className="text-sm font-medium">Rule Name</label>
                    <Input
                        value={formData.name}
                        onChange={e => setFormData({ ...formData, name: e.target.value })}
                        placeholder="e.g., Large Trade Alert"
                        required
                    />
                </div>
                <div>
                    <label className="text-sm font-medium">Trigger Event</label>
                    <Select
                        value={formData.trigger}
                        onValueChange={value => setFormData({ ...formData, trigger: value })}
                    >
                        <SelectTrigger>
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                            {availableTriggers.map(trigger => (
                                <SelectItem key={trigger} value={trigger}>
                                    {triggerDescriptions[trigger] ||
                                        trigger
                                            .split('_')
                                            .map(
                                                word => word.charAt(0).toUpperCase() + word.slice(1)
                                            )
                                            .join(' ')}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </div>
            </div>

            {/* Show available variables when trigger is selected */}
            {formData.trigger && (
                <Card className="bg-info/10 border-info/20 text-foreground">
                    <CardHeader className="pb-3">
                        <CardTitle className="text-sm flex items-center gap-2">
                            <Info className="h-4 w-4" />
                            Available Variables for Template
                        </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-3">
                        <div>
                            <p className="text-xs font-semibold text-muted-foreground mb-2">
                                General Variables (available to all templates):
                            </p>
                            <div className="flex flex-wrap gap-1">
                                {GENERAL_VARIABLES.map(variable => (
                                    <Badge
                                        key={variable.name}
                                        variant="secondary"
                                        className="text-xs font-mono cursor-help"
                                        title={variable.description}
                                    >
                                        {`{{${variable.name}}}`}
                                    </Badge>
                                ))}
                            </div>
                        </div>
                        <div>
                            <p className="text-xs font-semibold text-muted-foreground mb-2">
                                Trigger-Specific Variables:
                            </p>
                            <div className="flex flex-wrap gap-1">
                                {getTriggerVariables(formData.trigger)
                                    .filter(v => !GENERAL_VARIABLES.some(gv => gv.name === v.name))
                                    .map(variable => (
                                        <Badge
                                            key={variable.name}
                                            variant="outline"
                                            className="text-xs font-mono cursor-help"
                                            title={variable.description}
                                        >
                                            {`{{${variable.name}}}`}
                                        </Badge>
                                    ))}
                            </div>
                        </div>
                        <p className="text-xs text-muted-foreground italic">
                            💡 Tip: Hover over a variable to see its description. Use these
                            variables in your notification template with the{' '}
                            <code className="bg-muted px-1 rounded">{`{{variable_name}}`}</code>{' '}
                            syntax.
                        </p>
                    </CardContent>
                </Card>
            )}

            <div>
                <label className="text-sm font-medium">Notification Template</label>
                <Select
                    value={formData.template_id}
                    onValueChange={value => setFormData({ ...formData, template_id: value })}
                >
                    <SelectTrigger>
                        <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                        {templates
                            .filter(t => t.type === 'automatic')
                            .map(template => (
                                <SelectItem key={template.id} value={template.id}>
                                    {template.name}
                                </SelectItem>
                            ))}
                    </SelectContent>
                </Select>
            </div>

            <div>
                <label className="text-sm font-medium mb-2 block">Recipients</label>
                <div className="grid grid-cols-2 gap-2">
                    {recipientOptions.map(recipient => (
                        <div key={recipient.value} className="flex items-center space-x-2">
                            <Checkbox
                                id={recipient.value}
                                checked={formData.recipients.includes(recipient.value)}
                                onCheckedChange={checked => {
                                    if (checked) {
                                        setFormData({
                                            ...formData,
                                            recipients: [...formData.recipients, recipient.value],
                                        });
                                    } else {
                                        setFormData({
                                            ...formData,
                                            recipients: formData.recipients.filter(
                                                r => r !== recipient.value
                                            ),
                                        });
                                    }
                                }}
                            />
                            <label htmlFor={recipient.value} className="text-sm">
                                {recipient.label}
                            </label>
                        </div>
                    ))}
                </div>
            </div>

            <div className="flex items-center space-x-2">
                <Switch
                    checked={formData.is_active}
                    onCheckedChange={checked => setFormData({ ...formData, is_active: checked })}
                />
                <label className="text-sm font-medium">Active</label>
            </div>

            <div className="flex justify-end gap-2">
                <Button type="button" variant="outline">
                    Cancel
                </Button>
                <Button type="submit">Update Rule</Button>
            </div>
        </form>
    );
};

// Test Rule Form Component
const TestRuleForm: React.FC<{
    rule: NotificationRule;
    onSubmit: (data: any) => void;
}> = ({ rule, onSubmit }) => {
    const [testData, setTestData] = useState<Record<string, any>>({});

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        onSubmit(testData);
    };

    const getSampleData = () => {
        switch (rule.trigger) {
            case 'trade_opened':
                return {
                    customer_id: '12345',
                    customer_name: 'John Doe',
                    instrument: 'EUR/USD',
                    trade_value: 15000,
                    volume: 1000,
                    margin_level: 85,
                };
            case 'customer_registered':
                return {
                    customer_id: '67890',
                    customer_name: 'Jane Smith',
                    account_type: 'premium',
                    email: 'jane@example.com',
                };
            case 'kyc_expiry':
                return {
                    customer_id: '11111',
                    customer_name: 'Bob Johnson',
                    document_type: 'passport',
                    days_until_expiry: 15,
                    verification_status: 'pending',
                };
            default:
                return {};
        }
    };

    return (
        <form onSubmit={handleSubmit} className="space-y-4">
            <div>
                <label className="text-sm font-medium mb-2 block">Test Data</label>
                <Textarea
                    value={JSON.stringify(testData, null, 2)}
                    onChange={e => {
                        try {
                            setTestData(JSON.parse(e.target.value));
                        } catch (error) {
                            // Invalid JSON, ignore
                        }
                    }}
                    placeholder="Enter test data as JSON"
                    rows={8}
                />
            </div>

            <div className="flex gap-2">
                <Button
                    type="button"
                    variant="outline"
                    onClick={() => setTestData(getSampleData())}
                >
                    Load Sample Data
                </Button>
                <Button type="button" variant="outline" onClick={() => setTestData({})}>
                    Clear
                </Button>
            </div>

            <div className="flex justify-end gap-2">
                <Button type="button" variant="outline">
                    Cancel
                </Button>
                <Button type="submit">Send Test Notification</Button>
            </div>
        </form>
    );
};
